import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const closeModal = () => setActiveModal(null);
  return <>
      <footer className="bg-deep-teal py-8" data-id="element-45">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-46">
          <div className="flex items-center justify-center gap-6 text-sm" data-id="element-47">
            <button onClick={() => setActiveModal('privacy')} className="text-white hover:text-gold transition-colors underline underline-offset-2" data-id="element-48">
              Privacy Policy
            </button>
            <span className="text-white/50" data-id="element-49">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-white hover:text-gold transition-colors underline underline-offset-2" data-id="element-50">
              Terms of Service
            </button>
          </div>
          <p className="mt-4 text-xs text-white/70" data-id="element-51">
            © {new Date().getFullYear()} The Nighttime Cortisol Code. All
            rights reserved.
          </p>
          <p className="mt-2 text-xs text-white/70" data-id="element-52">
            This product does not replace professional medical advice.
          </p>
        </div>
      </footer>

      {/* Modal Overlay */}
      <AnimatePresence data-id="element-53">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={closeModal} data-id="element-54">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-55">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-cream/50" data-id="element-56">
                <h3 className="text-xl font-bold text-deep-teal font-heading" data-id="element-57">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h3>
                <button onClick={closeModal} className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-deep-teal" aria-label="Close" data-id="element-58">
                  <XIcon className="w-5 h-5" data-id="element-59" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)] text-body text-sm leading-relaxed space-y-4" data-id="element-60">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-61" /> : <TermsContent data-id="element-62" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-muted text-xs uppercase tracking-wider font-semibold" data-id="element-63">
        Last updated: March 2026
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg mt-4" data-id="element-64">
        1. Information We Collect
      </h4>
      <p data-id="element-65">
        When you purchase our digital product, we collect your name, email
        address, and payment information. Payment processing is handled by
        secure third-party processors — we never store your credit card details
        directly.
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg" data-id="element-66">
        2. How We Use Your Information
      </h4>
      <p data-id="element-67">
        We use your information solely to deliver your purchased digital
        product, send order confirmations, and provide customer support. We may
        also send occasional product updates or related health tips, which you
        can opt out of at any time.
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg" data-id="element-68">
        3. Data Sharing
      </h4>
      <p data-id="element-69">
        We do not sell, rent, or share your personal information with third
        parties for marketing purposes. Your data is only shared with payment
        processors and email delivery services necessary to fulfill your order.
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg" data-id="element-70">
        4. Cookies
      </h4>
      <p data-id="element-71">
        Our website may use cookies to improve your browsing experience and
        analyze site traffic. You can disable cookies in your browser settings
        at any time.
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg" data-id="element-72">
        5. Data Security
      </h4>
      <p data-id="element-73">
        We implement industry-standard security measures to protect your
        personal information, including SSL encryption for all data
        transmissions.
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg" data-id="element-74">
        6. Your Rights
      </h4>
      <p data-id="element-75">
        You have the right to access, correct, or delete your personal data at
        any time. To exercise these rights, please reach out through our support
        channels.
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg" data-id="element-76">
        7. Changes to This Policy
      </h4>
      <p data-id="element-77">
        We may update this Privacy Policy from time to time. Any changes will be
        posted on this page with an updated revision date.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-muted text-xs uppercase tracking-wider font-semibold" data-id="element-78">
        Last updated: March 2026
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg mt-4" data-id="element-79">
        1. Digital Product
      </h4>
      <p data-id="element-80">
        The Nighttime Cortisol Code is a digital information product delivered
        in PDF format. Upon purchase, you will receive immediate access via
        email. This product is for educational and informational purposes only.
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg" data-id="element-81">
        2. Medical Disclaimer
      </h4>
      <p data-id="element-82">
        This product is not intended to diagnose, treat, cure, or prevent any
        disease. The information provided is not a substitute for professional
        medical advice. Always consult your physician or qualified healthcare
        provider before starting any new health protocol, especially if you are
        on hormone replacement therapy or other medications.
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg" data-id="element-83">
        3. No Guarantees
      </h4>
      <p data-id="element-84">
        Results may vary from person to person. While the protocols in this
        guide are based on established physiological principles, we cannot
        guarantee specific outcomes. Individual results depend on many factors
        including adherence, overall health, and lifestyle.
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg" data-id="element-85">
        4. Intellectual Property
      </h4>
      <p data-id="element-86">
        All content within The Nighttime Cortisol Code is protected by
        copyright. You may not reproduce, distribute, or resell any part of this
        product without written permission.
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg" data-id="element-87">
        5. Refund Policy
      </h4>
      <p data-id="element-88">
        Due to the digital nature of this product, all sales are final. If you
        experience any issues accessing your purchase, please contact our
        support team and we will assist you promptly.
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg" data-id="element-89">
        6. Limitation of Liability
      </h4>
      <p data-id="element-90">
        To the fullest extent permitted by law, we shall not be liable for any
        indirect, incidental, or consequential damages arising from the use of
        this product.
      </p>

      <h4 className="text-deep-teal font-heading font-bold text-lg" data-id="element-91">
        7. Governing Law
      </h4>
      <p data-id="element-92">
        These terms shall be governed by and construed in accordance with
        applicable laws. Any disputes shall be resolved through appropriate
        legal channels.
      </p>
    </>;
}